import React, { useState, useEffect } from 'react';
import { db, collection, onSnapshot, query, where, addDoc, deleteDoc, doc, handleFirestoreError, OperationType, Timestamp } from '../firebase';
import { SavedFilter, ReportType, ReportStatus } from '../types';
import { useAuth } from '../App';
import { 
  Save, 
  Trash2, 
  Filter, 
  X, 
  Check, 
  Calendar, 
  User, 
  Briefcase, 
  RotateCcw, 
  SlidersHorizontal,
  FileText,
  Activity,
  ChevronDown
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import ConfirmationModal from './ConfirmationModal';

interface Filters {
  type: ReportType | 'all';
  status: ReportStatus | 'all';
  clientId: string;
  technicianId: string;
  dateFrom: string;
  dateTo: string;
}

interface Props {
  currentFilters: Filters;
  onApply: (filters: Filters) => void;
  clients: { id: string, name: string }[];
  technicians: { id: string, name: string }[];
}

const AdvancedFilters: React.FC<Props> = ({ currentFilters, onApply, clients, technicians }) => {
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'savedFilters'), where('userId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setSavedFilters(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SavedFilter)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'savedFilters');
    });
    return unsubscribe;
  }, [user]);

  const handleSaveFilter = async () => {
    if (!filterName || !user) return;
    try {
      await addDoc(collection(db, 'savedFilters'), {
        userId: user.uid,
        name: filterName,
        filters: currentFilters,
        createdAt: Timestamp.now()
      });
      setFilterName('');
      setIsSaving(false);
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'savedFilters');
    }
  };

  const handleDeleteFilter = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Saved Filter',
      message: 'Are you sure you want to delete this saved filter? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'savedFilters', id));
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `savedFilters/${id}`);
        }
      }
    });
  };

  const applyPreset = (preset: 'today' | '7days' | '30days' | 'month' | 'quarter' | 'year' | 'clear') => {
    const today = new Date();
    const formatDate = (d: Date) => d.toISOString().split('T')[0];
    
    let from = '';
    let to = formatDate(today);
    
    switch (preset) {
      case 'today':
        from = formatDate(today);
        break;
      case '7days': {
        const d = new Date();
        d.setDate(today.getDate() - 7);
        from = formatDate(d);
        break;
      }
      case '30days': {
        const d = new Date();
        d.setDate(today.getDate() - 30);
        from = formatDate(d);
        break;
      }
      case 'month': {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        from = formatDate(firstDay);
        break;
      }
      case 'quarter': {
        const currentQuarterMonth = Math.floor(today.getMonth() / 3) * 3;
        const firstDayOfQuarter = new Date(today.getFullYear(), currentQuarterMonth, 1);
        from = formatDate(firstDayOfQuarter);
        break;
      }
      case 'year': {
        const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
        from = formatDate(firstDayOfYear);
        break;
      }
      case 'clear':
        from = '';
        to = '';
        break;
    }
    
    onApply({
      ...currentFilters,
      dateFrom: from,
      dateTo: to
    });
  };

  const activeFiltersCount = [
    currentFilters.type !== 'all',
    currentFilters.status !== 'all',
    currentFilters.clientId !== 'all',
    currentFilters.technicianId !== 'all',
    !!currentFilters.dateFrom,
    !!currentFilters.dateTo,
  ].filter(Boolean).length;

  const handleResetAll = () => {
    onApply({
      type: 'all',
      status: 'all',
      clientId: 'all',
      technicianId: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  return (
    <div className="bg-slate-50/50 rounded-2.5xl p-5 border border-slate-100 space-y-5">
      {/* Filters Title Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-slate-500" />
          <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Advanced Filter Suite</h4>
          {activeFiltersCount > 0 && (
            <span className="bg-blue-100 text-blue-800 text-[10px] font-black px-2 py-0.5 rounded-full uppercase">
              {activeFiltersCount} Active
            </span>
          )}
        </div>
        {activeFiltersCount > 0 && (
          <button
            onClick={handleResetAll}
            className="flex items-center gap-1 text-[10px] font-black text-slate-500 hover:text-red-600 transition-colors uppercase tracking-wider"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Clear All
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* Type Filter */}
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-slate-400" />
            Type
          </label>
          <div className="relative">
            <select
              value={currentFilters.type}
              onChange={(e) => onApply({ ...currentFilters, type: e.target.value as any })}
              className="w-full px-3 py-2.5 bg-white border border-slate-200/80 rounded-xl text-xs font-bold text-slate-700 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all cursor-pointer"
            >
              <option value="all">All Types</option>
              <option value="inspection">Inspection</option>
              <option value="survey">Survey</option>
              <option value="general">General</option>
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>

        {/* Status Filter */}
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5 text-slate-400" />
            Status
          </label>
          <div className="relative">
            <select
              value={currentFilters.status}
              onChange={(e) => onApply({ ...currentFilters, status: e.target.value as any })}
              className="w-full px-3 py-2.5 bg-white border border-slate-200/80 rounded-xl text-xs font-bold text-slate-700 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="submitted_to_technical">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="submitted_to_billing">In Billing</option>
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>

        {/* Client Filter */}
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1.5">
            <Briefcase className="w-3.5 h-3.5 text-slate-400" />
            Client
          </label>
          <div className="relative">
            <select
              value={currentFilters.clientId}
              onChange={(e) => onApply({ ...currentFilters, clientId: e.target.value })}
              className="w-full px-3 py-2.5 bg-white border border-slate-200/80 rounded-xl text-xs font-bold text-slate-700 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all cursor-pointer"
            >
              <option value="all">All Clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>

        {/* Technician Filter */}
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1.5">
            <User className="w-3.5 h-3.5 text-slate-400" />
            Technician
          </label>
          <div className="relative">
            <select
              value={currentFilters.technicianId}
              onChange={(e) => onApply({ ...currentFilters, technicianId: e.target.value })}
              className="w-full px-3 py-2.5 bg-white border border-slate-200/80 rounded-xl text-xs font-bold text-slate-700 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all cursor-pointer"
            >
              <option value="all">All Technicians</option>
              {technicians.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>

        {/* Date From */}
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            From Date
          </label>
          <input
            type="date"
            value={currentFilters.dateFrom}
            onChange={(e) => onApply({ ...currentFilters, dateFrom: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all cursor-pointer"
          />
        </div>

        {/* Date To */}
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            To Date
          </label>
          <input
            type="date"
            value={currentFilters.dateTo}
            onChange={(e) => onApply({ ...currentFilters, dateTo: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all cursor-pointer"
          />
        </div>
      </div>

      {/* Date Range Presets Helper */}
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-[10px] font-bold text-slate-400 uppercase mr-1.5">Quick Date presets:</span>
        <button
          onClick={() => applyPreset('today')}
          className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 hover:border-blue-800 text-[10px] font-bold text-slate-600 hover:text-blue-800 transition"
        >
          Today
        </button>
        <button
          onClick={() => applyPreset('7days')}
          className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 hover:border-blue-800 text-[10px] font-bold text-slate-600 hover:text-blue-800 transition"
        >
          Last 7 Days
        </button>
        <button
          onClick={() => applyPreset('30days')}
          className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 hover:border-blue-800 text-[10px] font-bold text-slate-600 hover:text-blue-800 transition"
        >
          Last 30 Days
        </button>
        <button
          onClick={() => applyPreset('month')}
          className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 hover:border-blue-800 text-[10px] font-bold text-slate-600 hover:text-blue-800 transition"
        >
          This Month
        </button>
        <button
          onClick={() => applyPreset('quarter')}
          className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 hover:border-blue-800 text-[10px] font-bold text-slate-600 hover:text-blue-800 transition"
        >
          This Quarter
        </button>
        <button
          onClick={() => applyPreset('year')}
          className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 hover:border-blue-800 text-[10px] font-bold text-slate-600 hover:text-blue-800 transition"
        >
          This Year
        </button>
        {(currentFilters.dateFrom || currentFilters.dateTo) && (
          <button
            onClick={() => applyPreset('clear')}
            className="px-2.5 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-[10px] font-bold text-red-700 transition"
          >
            Clear Dates
          </button>
        )}
      </div>

      {/* Active Filter Badges */}
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap gap-2 items-center pt-3 border-t border-slate-200/50">
          <span className="text-[10px] font-bold text-slate-400 uppercase mr-1">Active Criteria:</span>
          {currentFilters.type !== 'all' && (
            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 px-2.5 py-1 rounded-lg text-[11px] font-bold border border-blue-100">
              Type: <strong className="capitalize">{currentFilters.type}</strong>
              <button onClick={() => onApply({ ...currentFilters, type: 'all' })} className="hover:text-red-500 transition-colors ml-1">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {currentFilters.status !== 'all' && (
            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 px-2.5 py-1 rounded-lg text-[11px] font-bold border border-blue-100">
              Status: <strong className="capitalize">{currentFilters.status === 'submitted_to_technical' ? 'Pending Approval' : currentFilters.status}</strong>
              <button onClick={() => onApply({ ...currentFilters, status: 'all' })} className="hover:text-red-500 transition-colors ml-1">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {currentFilters.clientId !== 'all' && (
            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 px-2.5 py-1 rounded-lg text-[11px] font-bold border border-blue-100">
              Client: <strong className="truncate max-w-[120px]">{clients.find(c => c.id === currentFilters.clientId)?.name || 'Selected'}</strong>
              <button onClick={() => onApply({ ...currentFilters, clientId: 'all' })} className="hover:text-red-500 transition-colors ml-1">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {currentFilters.technicianId !== 'all' && (
            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 px-2.5 py-1 rounded-lg text-[11px] font-bold border border-blue-100">
              Technician: <strong className="truncate max-w-[120px]">{technicians.find(t => t.id === currentFilters.technicianId)?.name || 'Selected'}</strong>
              <button onClick={() => onApply({ ...currentFilters, technicianId: 'all' })} className="hover:text-red-500 transition-colors ml-1">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {currentFilters.dateFrom && (
            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 px-2.5 py-1 rounded-lg text-[11px] font-bold border border-blue-100">
              From: <strong>{currentFilters.dateFrom}</strong>
              <button onClick={() => onApply({ ...currentFilters, dateFrom: '' })} className="hover:text-red-500 transition-colors ml-1">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {currentFilters.dateTo && (
            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 px-2.5 py-1 rounded-lg text-[11px] font-bold border border-blue-100">
              To: <strong>{currentFilters.dateTo}</strong>
              <button onClick={() => onApply({ ...currentFilters, dateTo: '' })} className="hover:text-red-500 transition-colors ml-1">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Saved Filters Area */}
      <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-slate-200/50">
        <div className="flex items-center gap-1 text-[10px] font-black text-slate-400 uppercase tracking-wider mr-2">
          <Save className="w-3.5 h-3.5" />
          Saved Filters:
        </div>
        
        {savedFilters.map(sf => (
          <div key={sf.id} className="flex items-center gap-1 bg-white border border-slate-200 rounded-full pl-3 pr-1 py-1 shadow-sm hover:border-slate-300 transition-all group">
            <button
              onClick={() => onApply(sf.filters as Filters)}
              className="text-[10px] font-bold text-slate-600 hover:text-slate-900"
            >
              {sf.name}
            </button>
            <button
              onClick={() => handleDeleteFilter(sf.id)}
              className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}

        <button
          onClick={() => setIsSaving(true)}
          className="text-[10px] font-black text-blue-700 hover:text-white hover:bg-blue-800 bg-blue-50 px-4 py-2 rounded-full transition-all uppercase tracking-widest"
        >
          + Save Current Filter
        </button>

        <AnimatePresence>
          {isSaving && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="flex items-center gap-2 bg-blue-950 p-1 rounded-full shadow-xl shadow-blue-900/20"
            >
              <input
                autoFocus
                type="text"
                placeholder="Filter name..."
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                className="bg-transparent text-white text-[10px] px-3 py-1 focus:outline-none w-32 font-bold"
              />
              <button onClick={handleSaveFilter} className="p-1 text-orange-500 hover:bg-white/10 rounded-full">
                <Check className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setIsSaving(false)} className="p-1 text-slate-400 hover:bg-white/10 rounded-full">
                <X className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="YES"
        cancelLabel="NO"
        isDanger={true}
      />
    </div>
  );
};

export default AdvancedFilters;
